/**
 * Dialog component for displaying node details
 */

import { Info as InfoIcon } from 'lucide-react';
import React, { useState } from 'react';
import { DialogState } from '../types';

type InfoTooltipProps = {
  text: string;
  label?: string;
};

const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, label }) => {
  const [open, setOpen] = useState(false);
  return (
    <span
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
      aria-label={label ? `Info: ${label}` : 'Info'}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
    >
      <InfoIcon size={14} color="#6b7280" />
      {open ? (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            top: '-6px',
            left: '18px',
            transform: 'translateY(-100%)',
            background: '#111827',
            color: '#ffffff',
            padding: '6px 8px',
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.2,
            maxWidth: 400,
            minWidth: 200,
            boxShadow: '0 6px 14px rgba(0,0,0,0.25)',
            zIndex: 60,
            whiteSpace: 'normal',
          }}
        >
          {text}
        </span>
      ) : null}
    </span>
  );
};

export type InfoDialogProps = {
  state: DialogState;
  onClose: () => void;
};

export const InfoDialog: React.FC<InfoDialogProps> = ({ state, onClose }) => {
  if (!state?.open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(680px, 92vw)',
          background: 'hsl(var(--popover))',
          color: 'hsl(var(--popover-foreground))',
          borderRadius: 12,
          border: '1px solid hsl(var(--border))',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          overflow: 'visible',
        }}
      >
        <div
          style={{
            padding: '14px 16px',
            borderBottom: '1px solid hsl(var(--border))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ fontWeight: 700, color: 'hsl(var(--popover-foreground))' }}>{state.title}</div>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 16,
              color: 'hsl(var(--muted-foreground))',
            }}
          >
            ✕
          </button>
        </div>
        <div
          style={{
            padding: 16,
            maxHeight: '410px',
            overflow: 'hidden',
            overflowY: 'auto',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 10 }}>
            {state.fields.map((f, idx) => (
              <div
                key={idx}
                style={
                  f.label
                    ? { display: 'grid', gridTemplateColumns: '200px 1fr', columnGap: 12, alignItems: 'start' }
                    : { display: 'block' }
                }
              >
                {f.label && (
                  <div
                    style={{
                      color: 'hsl(var(--muted-foreground))',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>{f.label}</span>
                    {f.help ? <InfoTooltip text={f.help} label={f.label} /> : null}
                  </div>
                )}
                <div style={{ whiteSpace: 'pre-wrap', color: 'hsl(var(--popover-foreground))' }}>{f.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
